import {
    MessageOutlined,
    CloseOutlined,
    SearchOutlined,
    FileTextOutlined,
    DollarCircleOutlined,
    CheckCircleOutlined,
    LockOutlined,
    ReloadOutlined,
    ArrowLeftOutlined,
} from '@ant-design/icons';
import {
    Drawer,
    List,
    Input,
    Typography,
    Tag,
    Space,
    Empty,
    Spin,
    Row,
    Col,
    Card,
    Badge,
    message,
    Button,
    Modal,
    Tooltip,
    Form,
} from 'antd';
import { createStyles } from 'antd-style';
import React, { useState, useEffect, useRef } from 'react';
import { getChatRoomList } from '@/services/ant-design-pro/chat-room';
import { confirmOrderPayment } from '@/services/ant-design-pro/order';
import { ChatRoom, ChatRoomRef } from '@/components/ChatRoom';
// TODO: 实现真实的token获取方法，可能从localStorage、cookie或状态管理中获取
const getToken = () => window.localStorage.getItem('token') || 'temp-token';

const { Search } = Input;
const { Text, Title } = Typography;

const useStyles = createStyles(({ token }) => {
    return {
        chatDrawer: {
            '& .ant-drawer-body': {
                padding: 0,
            },
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                '& .ant-drawer-content-wrapper': {
                    width: '100% !important',
                },
            },
        },
        '@keyframes pulse': {
            '0%': {
                opacity: 1,
            },
            '50%': {
                opacity: 0.7,
            },
            '100%': {
                opacity: 1,
            },
        },
        chatContainer: {
            display: 'flex',
            height: '100%',
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                flexDirection: 'column',
            },
        },
        chatList: {
            width: '280px',
            borderRight: `1px solid ${token.colorBorderSecondary}`,
            display: 'flex',
            flexDirection: 'column',
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                width: '100%',
                borderRight: 'none',
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
            },
        },
        chatListMobile: {
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                height: '100% !important',
                opacity: '1',
                visibility: 'visible',
            },
        },
        chatListHidden: {
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                height: '0 !important',
                opacity: '0',
                visibility: 'hidden',
                padding: '0',
                margin: '0',
            },
        },
        chatListHeader: {
            padding: '12px 16px',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            backgroundColor: token.colorBgContainer,
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                padding: '12px',
            },
        },
        chatListContent: {
            flex: 1,
            overflow: 'hidden',
        },
        chatDetail: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                overflow: 'hidden',
                transition: 'all 0.3s ease',
            },
        },
        chatDetailMobile: {
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                height: '100% !important',
                opacity: '1',
                visibility: 'visible',
            },
        },
        chatDetailHidden: {
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                height: '0 !important',
                opacity: '0',
                visibility: 'hidden',
                padding: '0',
                margin: '0',
            },
        },
        chatDetailHeader: {
            padding: '12px 16px',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            backgroundColor: token.colorBgContainer,
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                padding: '12px',
            },
        },
        chatDetailContent: {
            flex: 1,
            padding: '12px 16px 16px 16px',
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                padding: '12px',
            },
        },
        listItem: {
            cursor: 'pointer',
            transition: 'background-color 0.3s ease',
            '&:hover': {
                backgroundColor: token.colorBgTextHover,
            },
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                padding: '16px !important',
                '&:active': {
                    backgroundColor: token.colorBgTextHover,
                },
            },
        },
        activeListItem: {
            backgroundColor: token.colorPrimaryBg,
            borderRight: `3px solid ${token.colorPrimary}`,
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                borderRight: 'none',
                borderBottom: `3px solid ${token.colorPrimary}`,
            },
        },
        orderInfo: {
            marginBottom: '8px',
        },
        lastMessage: {
            color: token.colorTextSecondary,
            fontSize: '12px',
        },
        placeholder: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: token.colorTextSecondary,
        },
        mobileBackButton: {
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                '&:hover': {
                    backgroundColor: token.colorBgTextHover,
                },
            },
            display: 'none',
        },
        mobileHeader: {
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
            },
            display: 'none',
        },
        mobileTitle: {
            [`@media screen and (max-width: ${token.screenMD}px)`]: {
                fontSize: '16px',
                fontWeight: 'bold',
                margin: 0,
            },
        },
    };
});

interface ChatDrawerProps {
    open: boolean;
    onClose: () => void;
}

export const ChatDrawer: React.FC<ChatDrawerProps> = ({ open, onClose }) => {
    const [chatRooms, setChatRooms] = useState<API.ChatRoomListItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const [selectedRoom, setSelectedRoom] = useState<API.ChatRoomListItem | null>(null);
    const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
    const [confirmModalVisible, setConfirmModalVisible] = useState<boolean>(false);
    const [confirmForm] = Form.useForm();
    const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
    const [autoRefreshCount, setAutoRefreshCount] = useState<number>(0);
    const [countdown, setCountdown] = useState<string>('');
    const [isMobile, setIsMobile] = useState<boolean>(false);
    const chatRoomRef = useRef<ChatRoomRef>(null);
    const { styles } = useStyles();

    // 检测移动端
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // 获取聊天室列表
    const fetchChatRooms = async (search?: string) => {
        setLoading(true);
        try {
            const response = await getChatRoomList({
                page: 1,
                limit: 50,
                order_no: search,
            });
            // console.log(response);
            if (response.code === 0 && response.data) {
                // 使用ChatRoomListDto类实例化数据
                // const chatRoomDtos = response.data.data.map(item => new API.ChatRoomListDto(item));
                setChatRooms(response.data.data);
                setLastRefreshTime(new Date());
            } else {
                message.error(response.msg || '获取聊天室列表失败');
            }
        } catch (error) {
            message.error('获取聊天室列表失败');
        } finally {
            setLoading(false);
        }
    };

    // 搜索处理
    const handleSearch = (value: string) => {
        setSearchValue(value);
        fetchChatRooms(value);
    };

    // 手动刷新
    const handleManualRefresh = () => {
        fetchChatRooms(searchValue);
    };

    // 选择聊天室
    const handleSelectRoom = (room: API.ChatRoomListItem) => {
        setSelectedRoom(room);
    };

    // 移动端返回列表
    const handleBackToList = () => {
        setSelectedRoom(null);
    };

    // 处理关闭抽屉
    const handleClose = () => {
        setSelectedRoom(null); // 清空选中的聊天室数据
        setSearchValue(''); // 清空搜索值
        onClose(); // 调用父组件的关闭函数
    };

    // 处理确认订单支付
    const handleConfirmPayment = () => {
        if (!selectedRoom) return;

        // 检查订单状态，如果不是待支付状态，不允许确认
        if (selectedRoom.status !== 'pending') {
            message.warning('只有待支付状态的订单才能确认支付');
            return;
        }

        // 显示确认支付模态框
        setConfirmModalVisible(true);
    };

    // 处理确认支付表单提交
    const handleConfirmPaymentSubmit = async (values: { password: string }) => {
        if (!selectedRoom) return;

        setConfirmLoading(true);
        try {
            const response = await confirmOrderPayment(String(selectedRoom.id), values.password);
            if (response.code === 0) {
                message.success('确认订单支付成功');

                // 关闭模态框并重置表单
                setConfirmModalVisible(false);
                confirmForm.resetFields();

                // 发送确认支付成功的消息
                if (chatRoomRef.current) {
                    chatRoomRef.current.sendMessage('已上分，如有不到账情况麻烦联系平台客服，谢谢！');
                }

                // 更新选中订单的状态为已支付
                const updatedRoom = { ...selectedRoom, status: 'paid' };
                setSelectedRoom(updatedRoom);

                // 更新聊天室列表中对应订单的状态
                setChatRooms(prevRooms =>
                    prevRooms.map(room =>
                        room.id === selectedRoom.id
                            ? { ...room, status: 'paid' }
                            : room
                    )
                );

                // 刷新聊天室列表以获取最新数据
                fetchChatRooms(searchValue);
            } else {
                message.error(response.msg || '确认订单支付失败');
            }
        } catch (error) {
            message.error('确认订单支付失败');
        } finally {
            setConfirmLoading(false);
        }
    };

    // 处理取消确认支付
    const handleCancelConfirmPayment = () => {
        setConfirmModalVisible(false);
        confirmForm.resetFields();
    };

    // 获取订单状态显示
    const getOrderStatusDisplay = (status: string) => {
        const statusMap = {
            'pending': { text: '待支付', color: 'orange' },
            'paid': { text: '已支付', color: 'green' },
            'cancelled': { text: '已取消', color: 'red' },
            'refunded': { text: '已退款', color: 'purple' },
        };
        const config = statusMap[status as keyof typeof statusMap] || { text: status, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    // 检查是否显示确认支付按钮（只有待支付状态才显示）
    const shouldShowConfirmButton = (status: string) => {
        return status === 'pending';
    };

    // 解析并渲染客户信息
    const renderCustomerInfo = (extParams: string) => {
        try {
            const parsedParams = JSON.parse(extParams);
            if (parsedParams.name) {
                return (
                    <Space>
                        <Text type="secondary">客户信息:</Text>
                        <Text style={{ color: '#333', fontWeight: 'bold' }}>
                            {parsedParams.name}
                        </Text>
                    </Space>
                );
            }
        } catch (error) {
            console.warn('解析客户信息失败:', error);
        }
        return null;
    };

    // 计算倒计时
    const calculateCountdown = (expireAt: Date) => {
        const now = new Date();
        const expireTime = new Date(expireAt);
        const diff = expireTime.getTime() - now.getTime();

        if (diff <= 0) {
            return '已过期';
        }

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        if (hours > 0) {
            return `${hours}时${minutes}分${seconds}秒`;
        } else if (minutes > 0) {
            return `${minutes}分${seconds}秒`;
        } else {
            return `${seconds}秒`;
        }
    };

    // 获取倒计时样式
    const getCountdownStyle = (countdownText: string) => {
        if (countdownText === '已过期') {
            return {
                backgroundColor: '#ff4d4f',
                color: '#fff',
            };
        }

        // 检查是否在5分钟内过期
        if (selectedRoom && selectedRoom.expire_at) {
            const now = new Date();
            const expireTime = new Date(selectedRoom.expire_at);
            const diff = expireTime.getTime() - now.getTime();
            const fiveMinutes = 5 * 60 * 1000; // 5分钟

            if (diff <= fiveMinutes && diff > 0) {
                return {
                    backgroundColor: '#ff7875',
                    color: '#fff',
                    animation: 'pulse 1s infinite',
                };
            }
        }

        return {
            backgroundColor: '#faad14',
            color: '#fff',
        };
    };

    // 倒计时定时器
    useEffect(() => {
        let countdownInterval: NodeJS.Timeout | null = null;

        if (selectedRoom && selectedRoom.expire_at && selectedRoom.status === 'pending') {
            // 立即计算一次
            setCountdown(calculateCountdown(selectedRoom.expire_at));

            // 每秒更新倒计时
            countdownInterval = setInterval(() => {
                const countdownText = calculateCountdown(selectedRoom.expire_at);
                setCountdown(countdownText);

                // 检查是否即将过期（5分钟内）
                if (selectedRoom && selectedRoom.expire_at) {
                    const now = new Date();
                    const expireTime = new Date(selectedRoom.expire_at);
                    const diff = expireTime.getTime() - now.getTime();
                    const fiveMinutes = 5 * 60 * 1000; // 5分钟

                    if (diff <= fiveMinutes && diff > 0 && diff > (fiveMinutes - 1000)) {
                        // 只在第一次进入5分钟倒计时时显示通知
                        message.warning(`订单 ${selectedRoom.order_no} 将在5分钟内过期！`);
                    }
                }

                // 如果已过期，清除定时器
                if (countdownText === '已过期') {
                    if (countdownInterval) {
                        clearInterval(countdownInterval);
                    }
                    message.error(`订单 ${selectedRoom.order_no} 已过期！`);
                }
            }, 1000);
        } else {
            setCountdown('');
        }

        return () => {
            if (countdownInterval) {
                clearInterval(countdownInterval);
            }
        };
    }, [selectedRoom]);

    // 定时刷新聊天室列表
    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;

        if (open) {
            // 立即获取一次
            fetchChatRooms();

            // 设置定时器，每30秒刷新一次
            intervalId = setInterval(() => {
                // 静默刷新，不显示loading状态
                const silentRefresh = async () => {
                    try {
                        const response = await getChatRoomList({
                            page: 1,
                            limit: 50,
                            order_no: searchValue,
                        });
                        if (response.code === 0 && response.data) {
                            setChatRooms(response.data.data);
                            setLastRefreshTime(new Date());
                            setAutoRefreshCount(prev => prev + 1);
                        }
                    } catch (error) {
                        console.error('自动刷新失败:', error);
                    }
                };
                silentRefresh();
            }, 5000); // 5秒
        }

        // 清理函数
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [open, searchValue]);

    return (
        <Drawer
            title={
                isMobile && selectedRoom ? (
                    <div className={styles.mobileHeader}>
                        <div className={styles.mobileBackButton} onClick={handleBackToList}>
                            <ArrowLeftOutlined />
                            <span>返回</span>
                        </div>
                        <div className={styles.mobileTitle}>
                            {selectedRoom.order_no}
                        </div>
                        <div></div>
                    </div>
                ) : (
                    <Space>
                        <MessageOutlined />
                        <span>聊天管理</span>
                    </Space>
                )
            }
            placement="right"
            width={isMobile ? '100%' : 900}
            open={open}
            onClose={handleClose}
            className={styles.chatDrawer}
            keyboard={false}
            mask={false}
            maskClosable={false}
            extra={
                <CloseOutlined onClick={handleClose} style={{ cursor: 'pointer' }} />
            }
        >
            <div className={styles.chatContainer}>
                {/* 左侧聊天列表 */}
                <div className={`${styles.chatList} ${isMobile && selectedRoom ? styles.chatListHidden : styles.chatListMobile}`}>
                    <div className={styles.chatListHeader}>
                        <div style={{ marginBottom: '8px' }}>
                            <Search
                                placeholder="搜索订单号"
                                allowClear
                                onSearch={handleSearch}
                                style={{ marginBottom: 0 }}
                                prefix={<SearchOutlined />}
                            />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {lastRefreshTime ? `最后更新: ${lastRefreshTime.toLocaleTimeString()}` : '未更新'}
                                </Text>
                            </div>
                            <Button
                                type="text"
                                size="small"
                                icon={<ReloadOutlined spin={loading} />}
                                onClick={handleManualRefresh}
                                disabled={loading}
                                style={{ padding: '4px 8px' }}
                            >
                                刷新
                            </Button>
                        </div>
                    </div>
                    <div className={styles.chatListContent}>
                        <Spin spinning={loading}>
                            {chatRooms.length === 0 ? (
                                <Empty
                                    description="暂无订单"
                                    style={{ marginTop: '50px' }}
                                />
                            ) : (
                                <List
                                    dataSource={chatRooms}
                                    renderItem={(room) => (
                                        <List.Item
                                            className={`${styles.listItem} ${selectedRoom?.id === room.id ? styles.activeListItem : ''
                                                }`}
                                            onClick={() => handleSelectRoom(room)}
                                            style={{ padding: '10px 16px' }}
                                        >
                                            <div style={{ width: '100%' }}>
                                                <div className={styles.orderInfo}>
                                                    <Row justify="space-between" align="middle">
                                                        <Col>
                                                            <Space>
                                                                <FileTextOutlined />
                                                                <Text strong>{room.order_no}</Text>
                                                            </Space>
                                                        </Col>
                                                        <Col>
                                                            {room.has_unread_messages && (
                                                                <Badge count={1} size="small" />
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </div>

                                                <div style={{ marginBottom: '8px' }}>
                                                    <Row justify="space-between" align="middle">
                                                        <Col>
                                                            <Space>
                                                                <DollarCircleOutlined style={{ color: '#1890ff' }} />
                                                                <Text style={{ color: '#1890ff', fontWeight: 'bold' }}>
                                                                    {room.amount} {room.currency}
                                                                </Text>
                                                            </Space>
                                                        </Col>
                                                        <Col>
                                                            {room.status && getOrderStatusDisplay(room.status)}
                                                        </Col>
                                                    </Row>
                                                </div>

                                                {room.subject && (
                                                    <div style={{ marginBottom: '8px' }}>
                                                        <Text type="secondary" ellipsis style={{ fontSize: '12px' }}>
                                                            {room.subject}
                                                        </Text>
                                                    </div>
                                                )}

                                                {/* Last message functionality removed - not available in new DTO */}
                                            </div>
                                        </List.Item>
                                    )}
                                />
                            )}
                        </Spin>
                    </div>
                </div>

                {/* 右侧详情和聊天 */}
                <div className={`${styles.chatDetail} ${isMobile && selectedRoom ? styles.chatDetailMobile : isMobile ? styles.chatDetailHidden : ''}`}>
                    {selectedRoom ? (
                        <>
                            <div className={styles.chatDetailHeader}>
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        <Space direction="vertical" size={4}>
                                            <Title level={5} style={{ margin: 0 }}>
                                                {selectedRoom.order_no}
                                            </Title>
                                            <Space>
                                                <Text type="secondary">订单金额:</Text>
                                                <Text style={{ color: '#1890ff', fontWeight: 'bold' }}>
                                                    {selectedRoom.amount} {selectedRoom.currency}
                                                </Text>
                                                {selectedRoom.status && getOrderStatusDisplay(selectedRoom.status)}
                                            </Space>
                                            {/* 显示客户信息 */}
                                            {selectedRoom.ext_params && renderCustomerInfo(selectedRoom.ext_params)}
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            {shouldShowConfirmButton(selectedRoom.status) && countdown && (
                                                <div style={{
                                                    padding: '4px 12px',
                                                    borderRadius: '4px',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    ...getCountdownStyle(countdown)
                                                }}>
                                                    {countdown === '已过期' ? '已过期' : `剩余: ${countdown}`}
                                                </div>
                                            )}
                                            {shouldShowConfirmButton(selectedRoom.status) && (
                                                <Tooltip title="点击确认此订单已收到付款" placement="bottom">
                                                    <Button
                                                        type="primary"
                                                        icon={<CheckCircleOutlined />}
                                                        loading={confirmLoading}
                                                        onClick={handleConfirmPayment}
                                                        size="middle"
                                                        disabled={countdown === '已过期'}
                                                    >
                                                        确认订单支付
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </Space>
                                    </Col>
                                </Row>
                            </div>

                            <div className={styles.chatDetailContent}>
                                <Row gutter={16} style={{ height: '100%' }}>
                                    <Col span={24}>
                                        <Card title="聊天室" size="small" style={{ height: '100%' }}>
                                            <ChatRoom
                                                ref={chatRoomRef}
                                                orderId={String(selectedRoom.id)}
                                                orderNo={selectedRoom.order_no || ''}
                                                token={getToken()}
                                                onError={(error) => {
                                                    message.error(error);
                                                }}
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            </div>
                        </>
                    ) : (
                        <div className={styles.placeholder}>
                            <Space direction="vertical" style={{ textAlign: 'center' }}>
                                <MessageOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />
                                <Text type="secondary">请选择一个聊天室</Text>
                            </Space>
                        </div>
                    )}
                </div>
            </div>

            {/* 确认支付模态框 */}
            <Modal
                title={
                    <Space>
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        <span>确认订单支付</span>
                    </Space>
                }
                open={confirmModalVisible}
                onCancel={handleCancelConfirmPayment}
                footer={null}
                destroyOnClose
            >
                <div style={{ marginBottom: '16px' }}>
                    <p>您确定要确认以下订单的支付状态吗？</p>
                    <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                        <div><strong>订单号：</strong>{selectedRoom?.order_no}</div>
                        <div style={{ marginTop: '4px' }}><strong>订单金额：</strong>{selectedRoom?.amount} {selectedRoom?.currency}</div>
                        <div style={{ marginTop: '4px' }}><strong>当前状态：</strong>{selectedRoom?.status && getOrderStatusDisplay(selectedRoom.status)}</div>
                    </div>
                    <p style={{ marginTop: '12px', color: '#ff4d4f' }}>
                        <strong>注意：</strong>此操作将确认该订单已收到付款，请谨慎操作。
                    </p>
                </div>

                <Form
                    form={confirmForm}
                    onFinish={handleConfirmPaymentSubmit}
                    layout="vertical"
                >
                    <Form.Item
                        name="password"
                        label="确认密码"
                        rules={[
                            { required: true, message: '请输入确认密码' },
                            { min: 1, message: '密码不能为空' }
                        ]}
                    >
                        <Input.Password
                            placeholder="请输入确认密码"
                            prefix={<LockOutlined />}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleCancelConfirmPayment}>
                                取消
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={confirmLoading}
                                icon={<CheckCircleOutlined />}
                            >
                                确认支付
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Drawer>
    );
}; 